import { EPS as SHARED_EPS } from './epsilon.js';
const ALL_TRIGGER_TYPES = ['exact', 'lt', 'lte', 'gt', 'gte', 'range'];
/** The two a compass can express: a heading, or an arc between two headings. */
export const LOOP_TRIGGER_TYPES = ['exact', 'range'];
/**
 * A trigger string read into its parts, or `null` when it is not one.
 *
 * `allowed` narrows the vocabulary, which is how compass keeps rejecting `lt:30` outright rather
 * than accepting it here and having to handle a case its domain cannot express. Passing the block's
 * own tuple also keeps the RETURN type narrow, so a compass `switch` still needs only two arms and
 * the type checker still enforces that.
 *
 * A trigger with a non-finite first number is not a trigger — and `range` needs both, so
 * `range:5:` is rejected rather than silently read as a point at 5.
 */
export function parseValueTrigger(trigger, allowed = ALL_TRIGGER_TYPES) {
    const [type, v1, v2] = trigger.split(':');
    if (!allowed.includes(type))
        return null;
    const n1 = parseFloat(v1 ?? '');
    const n2 = parseFloat(v2 ?? '');
    if (!Number.isFinite(n1))
        return null;
    if (type === 'range' && !Number.isFinite(n2))
        return null;
    return { type: type, n1, n2 };
}
/**
 * Whether a value fires this trigger.
 *
 * `exact` compares within an epsilon and the four thresholds do not, which is deliberate on both
 * counts: an exact trigger names a point a float may only approximate, while `lt`/`lte` name a
 * boundary whose sharpness is the entire reason a teacher picked it. `range` accepts its ends in
 * either order, so `range:8:2` is the same band as `range:2:8`.
 *
 * The epsilon is INJECTED rather than chosen here. Numerical and slider pass the shared
 * `16 * Number.EPSILON`; angle passes its own `1e-9`, which is safe on a fixed 0-360 domain and
 * would be a grading change to touch. See `epsilon.ts` for why the two exist.
 */
export function matchesValueTrigger(trigger, value, eps = SHARED_EPS) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return false;
    const { type, n1, n2 } = parsed;
    switch (type) {
        case 'exact': return Math.abs(value - n1) < eps(n1);
        case 'lt': return value < n1;
        case 'lte': return value <= n1;
        case 'gt': return value > n1;
        case 'gte': return value >= n1;
        case 'range': return value >= Math.min(n1, n2) && value <= Math.max(n1, n2);
    }
}
/**
 * The interval a trigger covers, with each end's own boundary.
 *
 * `domainLo`/`domainHi` are where a ONE-SIDED trigger stops — the only thing that differed between
 * the four copies this replaces. The defaults are the unbounded case; a block with a real domain
 * passes it (angle `0, 360`; gps `0, Infinity`).
 *
 * The inclusive flags are not decoration. `lt` produces a hi-exclusive end, and that is what stops
 * `lt:5` and `gt:5` from merging into "everything" and declaring Default Incorrect unreachable when
 * exactly 5 still reaches it. Note the deliberate asymmetry: the OPEN end of a one-sided trigger is
 * exclusive, while the end that merely runs to the domain edge is inclusive, because the domain
 * boundary is a real reachable value rather than a limit.
 */
export function triggerToBoundedInterval(trigger, domainLo = -Infinity, domainHi = Infinity) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return null;
    const { type, n1, n2 } = parsed;
    if (type === 'exact')
        return { lo: n1, hi: n1, loInclusive: true, hiInclusive: true };
    if (type === 'lt')
        return { lo: domainLo, hi: n1, loInclusive: true, hiInclusive: false };
    if (type === 'lte')
        return { lo: domainLo, hi: n1, loInclusive: true, hiInclusive: true };
    if (type === 'gt')
        return { lo: n1, hi: domainHi, loInclusive: false, hiInclusive: true };
    if (type === 'gte')
        return { lo: n1, hi: domainHi, loInclusive: true, hiInclusive: true };
    return { lo: Math.min(n1, n2), hi: Math.max(n1, n2), loInclusive: true, hiInclusive: true }; // range
}
/*
 * ── Why `containsValue` is NOT here ───────────────────────────────────────────────────────────
 *
 * Numerical and slider both have one, they differ by an epsilon on the inclusive ends, and the
 * census's first read was that one of them had simply drifted. It is not drift. The two blocks
 * receive values of different PROVENANCE:
 *
 *   - a numerical value is TYPED. "0.3" parses to exactly the bound it is compared against, so
 *     numerical cleans each computed bound (`cleanComputed`, 9 sites) and then compares exactly —
 *     which is what lets "at most 5.5" reject 5.500000001, the answer a teacher expects.
 *   - a slider value is COMPUTED, as `min + k * step`. It is float-dirty by construction and can
 *     never be relied on to land exactly on a bound, so slider carries the tolerance in the
 *     comparison instead.
 *
 * Two coherent strategies for one problem, each right for its own input. Merging them would break
 * whichever block lost. Recorded here because it took reading both to tell it apart from drift, and
 * because numerical's own comment claimed the opposite (that `containsValue` "already applies the
 * epsilon to inclusive ends only") for long enough to mislead this census.
 */
//# sourceMappingURL=value-trigger.js.map